import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  getProductDetail,
  createProduct,
  getBestSellerProduct,
  getScreensaverData,
  getStoreDataAction,
  addFavourite,
  getFavourites,
  deleteFavourite
} from '../../actions/customizer';
import { RootState } from '../../store';
import { AnyObject } from '../../../utils/basic';
import { IFavourite } from '../../types/builder';
import AuthManager from '../../../utils/AuthManager';
import { bsLog } from '../../../utils/bestSeller';

export interface CustomizerState {
  isFetching: boolean;
  product?: any;
  createdProduct: null | AnyObject;
  bestSellerProduct: null | AnyObject;
  // Request id of the newest in-flight best seller request, used to discard
  // responses from superseded requests (see the getBestSellerProduct cases).
  bestSellerRequestId?: string;
  // The bestseller id that request is for, and whether it failed. The modal
  // reads both to decide between its product, its loading placeholder and its
  // error state, so that every request it makes resolves into one of the three.
  bestSellerPendingId?: string;
  bestSellerError?: boolean;
  screensaverData: null | AnyObject;
  storeData: null | AnyObject;
  selectedShippingPriority?: string;
  favourites: IFavourite[];
}

const initialState: CustomizerState = {
  isFetching: false,
  product: undefined,
  createdProduct: null,
  bestSellerProduct: null,
  screensaverData: null,
  storeData: null,
  selectedShippingPriority: undefined,
  favourites: []
};

export const customizerSlice = createSlice({
  name: 'customizer',
  initialState: initialState,
  reducers: {
    setProductData: (state: CustomizerState, action: PayloadAction<object>) => {
      state.product = action.payload;
    },
    resetCreatedProduct: (state: CustomizerState) => {
      state.createdProduct = null;
    },
    setBestSellerProduct: (state: CustomizerState, action: PayloadAction<object>) => {
      state.bestSellerProduct = action.payload;
    },
    setSelectedShippingPriority: (state: CustomizerState, action: PayloadAction<string>) => {
      state.selectedShippingPriority = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProductDetail.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(getProductDetail.fulfilled, (state, action) => {
        state.isFetching = false;
        state.product = action.payload;
      })
      .addCase(getProductDetail.rejected, (state) => {
        state.isFetching = false;
      })
      .addCase(createProduct.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(createProduct.fulfilled, (state, action) => {
        state.isFetching = false;
        state.createdProduct = action.payload;
      })
      .addCase(createProduct.rejected, (state) => {
        state.isFetching = false;
      })
      .addCase(getBestSellerProduct.pending, (state, action) => {
        state.isFetching = true;
        state.bestSellerRequestId = action.meta.requestId;
        state.bestSellerPendingId = String(action.meta.arg?.id ?? '');
        state.bestSellerError = false;
        bsLog('request started', { id: action.meta.arg?.id, requestId: action.meta.requestId });
      })
      .addCase(getBestSellerProduct.fulfilled, (state, action) => {
        // Switching best seller variants (the weight buttons in the Ready to Ship
        // modal) can leave more than one request in flight, and responses are not
        // guaranteed to arrive in request order. Only the newest request may write
        // to the store: an older response landing last would leave the store
        // holding a product that no longer matches the requested bestseller id,
        // and the modal — which treats that mismatch as "still loading" — would
        // sit on its loading placeholder forever.
        if (state.bestSellerRequestId && action.meta.requestId !== state.bestSellerRequestId) {
          bsLog('response dropped (superseded)', {
            id: action.meta.arg?.id,
            returned: action.payload?.bestseller_id
          });
          return;
        }
        bsLog('response applied', { requested: action.meta.arg?.id, returned: action.payload?.bestseller_id });
        state.isFetching = false;
        state.bestSellerPendingId = undefined;
        state.bestSellerProduct = action.payload;
      })
      .addCase(getBestSellerProduct.rejected, (state, action) => {
        if (state.bestSellerRequestId && action.meta.requestId !== state.bestSellerRequestId) return;
        bsLog('request failed', { id: action.meta.arg?.id });
        state.isFetching = false;
        state.bestSellerPendingId = undefined;
        state.bestSellerError = true;
      })
      .addCase(getScreensaverData.fulfilled, (state, action) => {
        state.screensaverData = action.payload;
      })
      .addCase(getScreensaverData.rejected, (state) => {
        state.screensaverData = null;
      })
      .addCase(getStoreDataAction.fulfilled, (state, action) => {
        state.storeData = action.payload;
      })
      .addCase(getStoreDataAction.rejected, (state) => {
        state.storeData = null;
      })
      .addCase(addFavourite.fulfilled, (_state, action: any) => {
        AuthManager.setAnonymousUserIfNotExists(action.payload.user_id);
      })
      .addCase(getFavourites.fulfilled, (state, action: any) => {
        state.favourites = action.payload?.result === true ? action.payload?.data : [];
      })
      .addCase(getFavourites.rejected, (state, action: any) => {
        if (action?.payload?.status === 401) {
          state.favourites = [];
        }
      })
      .addCase(deleteFavourite.fulfilled, (state, action) => {
        const deletedUrl = action.meta.arg.product_url;
        state.favourites = state.favourites.filter((f) => f.product_url !== deletedUrl);
      });
  }
});

export const { setProductData, resetCreatedProduct, setSelectedShippingPriority } = customizerSlice.actions;

const customizerReducer = customizerSlice.reducer;

export const selectProduct = (state: RootState) => {
  return state.customizer.product;
};

export const selectCreatedProduct = (state: RootState) => {
  return state.customizer.createdProduct;
};

export const selectSelectedShippingPriority = (state: RootState) => {
  return state.customizer.selectedShippingPriority;
};
export const selectStoreData = (state: RootState) => {
  return state.customizer.storeData;
};
export default customizerReducer;
